import React, { useState, useEffect } from "react";
import Sidebar from "../Sidebar/Sidebar";
import Navbar from "../Navbar/Navbar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
// import { Dropdown } from "primereact/dropdown";
// import { MultiSelect } from "primereact/multiselect";
import MultiSelectDropdown from "../components/MultiSelectDropdown";
import { axiosConfig } from "../axiosAPI";
import Swal from "sweetalert2";
// import { useAuth } from "../AuthProvider";
import PermissionGate from "../components/PermissionGate";
import "../Assets/Css/FormImprovements.css";

function Roles() {
  const [roles, setRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  
  const [formData, setFormData] = useState({
    uuid: null,
    displayName_En: "",
    displayName_Ar: "",
    status: "Active",
    permissions: [],
  });

  // const auth = useAuth();

  // Fetch roles
  const getRoles = async () => {
    setLoading(true);
    try {
      const response = await axiosConfig.get(`${process.env.REACT_APP_BASE_URL}/roles`, {
        params: { pageNo: 1, rowCount: 100 },
      });
      setRoles(response.data.result);
      setFilteredRoles(response.data.result);
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error", text: "Failed to fetch roles" });
    } finally {
      setLoading(false);
    }
  };

  // Fetch permission groups
  const getPermissionGroups = async () => {
    try {
      const response = await axiosConfig.get(`${process.env.REACT_APP_BASE_URL}/roles/groups`);
      setPermissionGroups(response.data);
    } catch (error) {
      Swal.fire({ icon: "error", title: "Error", text: "Failed to fetch permissions" });
    }
  };

  // Create/Update role
  const handleSubmit = async () => {
    // Basic validation
    if (!formData.displayName_En.trim()) {
      Swal.fire({ icon: "error", title: "Error", text: "Role name in English is required" });
      return;
    }
    if (!formData.displayName_Ar.trim()) {
      Swal.fire({ icon: "error", title: "Error", text: "Role name in Arabic is required" });
      return;
    }

    try {
      if (editMode) {
        await axiosConfig.put(`${process.env.REACT_APP_BASE_URL}/roles/${formData.uuid}`, {
          displayName_En: formData.displayName_En,
          displayName_Ar: formData.displayName_Ar,
          status: formData.status,
          permissions: formData.permissions || [],
        });
        Swal.fire({ icon: "success", title: "Success", text: "Role updated successfully" });
      } else {
        await axiosConfig.post(`${process.env.REACT_APP_BASE_URL}/roles`, {
          displayName_En: formData.displayName_En,
          displayName_Ar: formData.displayName_Ar,
          status: formData.status,
          permissions: formData.permissions || [],
        });
        Swal.fire({ icon: "success", title: "Success", text: "Role created successfully" });
      }
      getRoles();
      setVisible(false);
      resetForm();
    } catch (error) {
      console.error("Role operation error:", error);
      Swal.fire({ icon: "error", title: "Error", text: "Operation failed" });
    }
  };

  // Delete role
  const deleteRole = async (uuid) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#E23904",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await axiosConfig.delete(`${process.env.REACT_APP_BASE_URL}/roles/${uuid}`);
        Swal.fire({ icon: "success", title: "Deleted", text: "Role deleted successfully" });
        getRoles();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to delete role" });
      }
    }
  };

  // Open edit dialog
  const openEditDialog = (roleData) => {
    setFormData({
      uuid: roleData.uuid,
      displayName_En: roleData.displayName_En || "",
      displayName_Ar: roleData.displayName_Ar || "",
      status: roleData.status || "Active",
      permissions: roleData.permissions || [],
    });
    setEditMode(true);
    setVisible(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      uuid: null,
      displayName_En: "",
      displayName_Ar: "",
      status: "Active",
      permissions: [],
    });
    setEditMode(false);
  };

  // Action buttons template
  const actionTemplate = (rowData) => (
    <div className="action-table">
      <PermissionGate permission="p_r0003">
        <a className="edit" onClick={() => openEditDialog(rowData)}>
          <i className="fa fa-pen"></i>
        </a>
      </PermissionGate>
      <PermissionGate permission="p_r0004">
        <a className="delete" onClick={() => deleteRole(rowData.uuid)}>
          <i className="fa fa-trash"></i>
        </a>
      </PermissionGate>
    </div>
  );

  // Status template
  const statusTemplate = (rowData) => (
    <span className={`status-badge ${rowData.status?.toLowerCase()}`}>
      {rowData.status}
    </span>
  );

  // Permission count template
  const permissionCountTemplate = (rowData) => (
    <span>{rowData.permissions?.length || 0}</span>
  );

  // Global filter
  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setGlobalFilterValue(value);
    
    if (value === '') {
      setFilteredRoles(roles);
    } else {
      const filtered = roles.filter(role => 
        role.displayName_En?.toLowerCase().includes(value.toLowerCase()) ||
        role.displayName_Ar?.toLowerCase().includes(value.toLowerCase()) ||
        role.status?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredRoles(filtered);
    }
  };

  useEffect(() => {
    getRoles();
    getPermissionGroups();
  }, []);

  const permissionOptions = (permissionGroups?.flatMap(group => 
    group.permissions?.map(permission => ({
      label: `${group.displayName} - ${permission.displayName}`,
      value: permission.identifier
    })) || []
  ) || []);

  return (
    <div className="main">
      <div className="sidebar"><Sidebar /></div>
      <div className="dev-table">
        <Navbar />
        <div className="table-card">
          <div className="table">
            <PermissionGate permission="p_r0002">
              <a className="add" onClick={() => { setEditMode(false); setVisible(true); }}>
                <i className="fa fa-plus"></i>
              </a>
            </PermissionGate>
            
            <div className="table-header">
              <h2>Roles & Permissions</h2>
              <div className="search-box">
                <InputText
                  value={globalFilterValue}
                  onChange={onGlobalFilterChange}
                  placeholder="Search roles..."
                  className="search-input"
                />
              </div>
            </div>

            <DataTable
              value={filteredRoles}
              paginator
              rows={10}
              loading={loading}
              globalFilter={globalFilterValue}
              emptyMessage="No roles found."
              className="roles-table"
            >
              <Column field="displayName_En" header="Role Name (EN)" sortable />
              <Column field="displayName_Ar" header="Role Name (AR)" sortable />
              <Column header="Permissions Count" body={permissionCountTemplate} sortable />
              <Column field="status" header="Status" body={statusTemplate} sortable />
              <Column field="dateCreated" header="Date Created" sortable />
              <Column header="Actions" body={actionTemplate} />
            </DataTable>

            {/* Add/Edit Role Dialog */}
            <Dialog
              header={editMode ? "Edit Role" : "Add New Role"}
              visible={visible}
              style={{ width: '600px', maxWidth: '90vw' }}
              onHide={() => { setVisible(false); resetForm(); }}
              className="role-dialog"
            >
              <div className="form-container" style={{ padding: '0' }}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Role Name (English)</label>
                    <InputText
                      value={formData.displayName_En}
                      onChange={(e) => setFormData({...formData, displayName_En: e.target.value})}
                      placeholder="Enter role name in English"
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Role Name (Arabic)</label>
                    <InputText
                      value={formData.displayName_Ar}
                      onChange={(e) => setFormData({...formData, displayName_Ar: e.target.value})}
                      placeholder="Enter role name in Arabic"
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="form-input"
                    >
                      <option value="" disabled>Select status</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group permissions-group">
                    <label className="form-label">Permissions</label>
                    <MultiSelectDropdown
                      value={formData.permissions || []}
                      options={permissionOptions}
                      onChange={(vals) => setFormData({ ...formData, permissions: vals || [] })}
                      placeholder="Select permissions"
                      className="permissions-multiselect"
                      maxSelectedLabels={3}
                      showSelectAll={true}
                      selectAllLabel="Select All Permissions"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <Button
                    label={editMode ? "Update Role" : "Create Role"}
                    onClick={handleSubmit}
                    className="submit-btn"
                  />
                  <Button
                    label="Cancel"
                    onClick={() => { setVisible(false); resetForm(); }}
                    className="cancel-btn"
                    outlined
                  />
                </div>
              </div>
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Roles;
